import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../axios";
import ServiceCalendar from "../components/ServiceCalendar";
import DutyChain from "../components/DutyChain";
import { isDarkColor } from "../utils/routeColor";
import "../styles/analysis.css";

const DAY_TYPE_LABEL = { weekday: "平日", sat: "土曜", holiday: "日祝" };

// 同じ路線が続く区間をひとかたまりにする。乗番は路線が変わる箇所が要点なので、
// 1便ずつ路線名を繰り返すより、変わり目が見えるほうが読みやすい。
function groupByRoute(trips) {
  const groups = [];
  for (const trip of trips) {
    const last = groups[groups.length - 1];
    if (last && last.route_id === trip.route_id) {
      last.trips.push(trip);
    } else {
      groups.push({
        route_id: trip.route_id,
        route: trip.route,
        route_full_name: trip.route_full_name,
        color: trip.route_color,
        is_foreign: trip.is_foreign_route,
        trips: [trip],
      });
    }
  }
  return groups;
}

/**
 * この乗番に入った車両を、日めくりではなくカレンダーで見せる。
 * 週の枠組みは ServiceCalendar に任せ、ここはセルの中身だけを組む。
 */
function BusCalendar({ entries, onPick }) {
  const byDate = new Map(entries.map((e) => [e.service_date, e]));

  return (
    <ServiceCalendar
      weeks={5}
      renderCell={({ key }) => {
        const entry = byDate.get(key);
        if (!entry) return null;

        // 途中で車が変わった日は、走った車を全部並べる
        return entry.buses.map((b) => (
          <button
            key={b.bus_number}
            // 1便でも当たれば「この乗番に入った」と言えるが、
            // 何便見えたかで確からしさは変わるので薄さで示す
            className={`anl-cal__bus${entry.confidence < 0.3 ? " anl-cal__bus--weak" : ""}${entry.changed ? " anl-cal__bus--changed" : ""}`}
            onClick={() => onPick({ ...entry, bus_number: b.bus_number })}
            title={`${b.bus_number} · ${entry.trip_count}便中${b.matched_trips}便を目撃${entry.changed ? "（当日は途中で車両が交代しています）" : ""}`}
          >
            {b.bus_number}
          </button>
        ));
      }}
    />
  );
}



export default function AnalysisDutyPage() {
  const { dutyId } = useParams();
  const navigate = useNavigate();

  const [duty, setDuty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/analysis/duties/${dutyId}`);
      setDuty(res.data);
    } catch (e) {
      setError(e.response?.status === 404 ? "その乗番は見つかりません。" : "取得に失敗しました");
      setDuty(null);
    } finally {
      setLoading(false);
    }
  }, [dutyId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="anl-page"><p className="anl-empty">読み込み中…</p></div>;
  if (error) return <div className="anl-page"><div className="anl-warning">{error}</div></div>;

  const routeGroups = groupByRoute(duty.trips);

  return (
    <div className="anl-page">
      <button
        className="anl-back"
        onClick={() => navigate(`/analysis/duties?depot=${encodeURIComponent(duty.depot)}&day_type=${duty.day_type}`)}
      >
        乗番一覧
      </button>

      <div className="anl-header">
        <h2 className="anl-title">
          乗番{duty.code}
          <span className="anl-badge anl-badge--depot">{duty.depot}</span>
          <span className={`anl-badge anl-badge--${duty.day_type}`}>{DAY_TYPE_LABEL[duty.day_type] ?? duty.day_type}</span>
        </h2>
        <p className="anl-subtitle">
          {duty.trip_count}便 · {duty.first_departure}〜{duty.last_departure}
          {duty.extracted_at && ` · ${duty.extracted_at}時点`}
        </p>
      </div>

      <div className="anl-card">
        <div className="anl-card__header">
          便の並び
          <span style={{ fontWeight: 400, color: "#6b7280" }}>
            {routeGroups.length === 1 ? "1路線" : `${routeGroups.length}区間`}
          </span>
        </div>
        <div className="anl-table-wrap">
          <table className="anl-table">
            <thead>
              <tr>
                <th>路線</th>
                <th className="anl-table__num">#</th>
                <th>発</th>
                <th>始発地</th>
                <th className="anl-table__num">目撃</th>
                <th>備考</th>
              </tr>
            </thead>
            <tbody>
              {routeGroups.map((group, gi) =>
                group.trips.map((t, ti) => {
                  const weakEdge = t.edge_share !== null && t.edge_share < 0.8;
                  const color = group.color || "#6b7280";

                  return (
                    <tr key={t.trip_id} className={ti === 0 && gi > 0 ? "anl-route-break" : undefined}>
                      {ti === 0 && (
                        <td rowSpan={group.trips.length} className="anl-route-cell">
                          <button
                            className="anl-route-badge"
                            style={{
                              background: color,
                              color: isDarkColor(color) ? "#fff" : "#111827",
                            }}
                            onClick={() => navigate(`/analysis/routes/${group.route_id}`)}
                            title={group.route_full_name}
                          >
                            {group.route}
                          </button>
                          <div className="anl-route-cell__count">{group.trips.length}便</div>
                          {group.is_foreign && (
                            <span className="anl-badge anl-badge--foreign">他所</span>
                          )}
                        </td>
                      )}
                      <td className="anl-table__num" style={{ color: "#9ca3af" }}>{t.seq}</td>
                      <td className="anl-table__num" style={{ fontWeight: 600 }}>{t.departure_time}</td>
                      <td>
                        {t.start}
                        {t.route_no && <span className="anl-no">{t.route_no}</span>}
                      </td>
                      <td className="anl-table__num">{t.observed_count}</td>
                      <td style={{ whiteSpace: "normal", fontSize: ".75rem", color: "#92400e" }}>
                        {weakEdge && <div>接続率{Math.round(t.edge_share * 100)}%</div>}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(duty.next_duties.length > 0 || duty.previous_duties.length > 0) && (
        <div className="anl-card">
          <div className="anl-card__header">前後の乗番</div>
          <div className="anl-card__body">
            <DutyChain
              self={duty}
              previous={duty.previous_duties}
              next={duty.next_duties}
              onOpen={(id) => navigate(`/analysis/duties/${id}`)}
            />
            <p className="anl-note">
              {duty.next_duties.length > 1
                ? "続きが複数あります。日によって移る乗番が異なるため、いずれになるかは判別できません。観測が蓄積されると傾向が明確になります。"
                : "同じ車両が1日のうちに担当した乗番の並びです。"}
            </p>
          </div>
        </div>
      )}

      <div className="anl-card">
        <div className="anl-card__header">
          この乗番に入った車両
          <span style={{ fontWeight: 400, color: "#6b7280" }}>直近5週</span>
        </div>
        {duty.recent_buses.length === 0 ? (
          <p className="anl-empty">記録がありません。</p>
        ) : (
          <div className="anl-card__body">
            <BusCalendar
              entries={duty.recent_buses}
              onPick={(b) =>
                navigate(`/analysis/buses/${encodeURIComponent(b.bus_number)}?date=${b.service_date}&depot=${encodeURIComponent(duty.depot)}`)
              }
            />
            {duty.recent_buses.some((b) => b.changed) && (
              <div className="anl-notice anl-notice--change" style={{ marginTop: ".625rem" }}>
                <strong>車両交代</strong> — 橙色の日は、途中で別の車両に交代しています。
              </div>
            )}
            <p className="anl-note">
              各セルの車号は、当日その乗番の便を運行していた車両です。淡い緑は目撃できた便が少ない日を示します
              （当該車両が入っていたことは確かですが、全区間を通して運行したかまでは判別できません）。
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
